import { useEffect } from 'react';
import { combine } from 'zustand/middleware';
import { createWithEqualityFn as create } from 'zustand/traditional';

import { getAudioSource } from '@/utils/audio';
import { Setter, createSetter } from '@/utils/zustand';

const storageKey = 'home-dashboard';

export interface StorageData {
  timerAlert: boolean;
  timerAlertAudio?: AudioBufferSourceNode;
}

interface StorageStore extends StorageData {
  setTimerAlert: Setter<StorageStore['timerAlert']>;
  playTimerAlertAudio: () => void;
  stopTimerAlertAudio: () => void;
  reset: () => void;
}

const defaults: StorageData = {
  timerAlert: false,
};

function loadFromStorage(): StorageData {
  const stored = typeof window !== 'undefined' ? localStorage.getItem(storageKey) : undefined;

  return {
    ...defaults,
    ...(stored ? JSON.parse(stored) : {}),
  };
}

export const useStorageStore = create(
  combine<StorageData, StorageStore>(loadFromStorage(), (set, get) => ({
    ...loadFromStorage(),
    setTimerAlert: createSetter(set, get, 'timerAlert'),
    playTimerAlertAudio: async () => {
      const audio = await getAudioSource('/assets/sounds/timer-alert.mp3');
      audio.start(0);
      set({ timerAlertAudio: audio });
    },
    stopTimerAlertAudio: () => {
      const audio = get().timerAlertAudio;
      if (audio) {
        audio.stop();
        set({ timerAlertAudio: undefined });
      }
    },
    reset: () => set(defaults),
  })),
);

export function StorageProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify({}));
  }, []);

  return children;
}
